import { computed } from 'vue';
import { getTextWithDryRun } from '../../../store/modules/schedulingFlow/helpers/index.js';
import { getSchedulingFlowConfirmButtonInfo } from '../../../store/modules/schedulingFlow/schedulingFlowSteps/confirmButton.js';

/** Helpers used internally, but not exported to consuming repos */
const getBackComponentAndProps = ({ store, route }) => store.getters['schedulingFlow/getCurrentStepBackComponent'](route);
/**
 * Returns back step in order. If one is not found (first step), returns
 * undefined and no back button will be shown.
 */
const getBackLocation = ({ store, route }) => { var _a; return (_a = store.getters['schedulingFlow/getBackStepRouteInOrder'](route)) !== null && _a !== void 0 ? _a : undefined; };
/**
 * Returns next step in order. If one is not found (last step), returns
 * undefined. The primary button will still show if there is a `confirmEvent`
 * available.
 */
const getNextLocation = ({ store, route }) => { var _a; return (_a = store.getters['schedulingFlow/getNextStepRouteInOrder'](route)) !== null && _a !== void 0 ? _a : undefined; };
const getNextDisabled = ({ store, route }) => computed(() => !store.getters['schedulingFlow/getIsCurrentStepValid'](route).value);
const getConfirmTooltip = ({ store, route }) => {
    const failures = store.getters['schedulingFlow/getCurrentStepValidationFailures'](route);
    return failures === null || failures === void 0 ? void 0 : failures.join('\n');
};
const getConfirmLoading = ({ store }) => store.state.schedulingFlow.localState.nextButtonLoading;
const getConfirmEvent = ({ store, route }) => {
    const hasCompletionAction = !!store.getters['schedulingFlow/getCurrentStepCompletionAction'](route);
    if (!hasCompletionAction)
        return;
    return () => store.dispatch('schedulingFlow/performCompletionAction');
};
const getSecondaryComponentAndProps = ({ store, route }) => store.getters['schedulingFlow/getCurrentStepSecondaryComponent'](route);
const getTertiaryComponentAndProps = ({ store, route }) => store.getters['schedulingFlow/getCurrentStepTertiaryComponent'](route);
const schedulingFlowHeadingWithNav = ({ backButtonText, confirmToOverride, hideBackButton, secondaryHeaderComponent, title, withGlobalFooter, withStickyHeader, confirmEventOverride, }) => (headingParams) => computed(() => {
    var _a, _b, _c, _d;
    const confirmTo = confirmToOverride !== null && confirmToOverride !== void 0 ? confirmToOverride : getNextLocation(headingParams);
    const backValues = {
        backComponent: (_a = getBackComponentAndProps(headingParams)) === null || _a === void 0 ? void 0 : _a.component,
        backComponentProps: (_b = getBackComponentAndProps(headingParams)) === null || _b === void 0 ? void 0 : _b.props,
        backLocation: getBackLocation(headingParams),
    };
    const { theme } = headingParams.store.state.app;
    const titleCopy = typeof title === 'object' ? title[theme] : title;
    const titleValues = {
        leftAlignTitle: true,
        title: titleCopy ? getTextWithDryRun(titleCopy, headingParams) : undefined,
    };
    const tertiaryComponentValues = {
        tertiaryComponent: (_c = getTertiaryComponentAndProps(headingParams)) === null || _c === void 0 ? void 0 : _c.component,
        tertiaryComponentProps: (_d = getTertiaryComponentAndProps(headingParams)) === null || _d === void 0 ? void 0 : _d.props,
    };
    /**
     * This is support for if we choose to pass the secondary component via
     * the route
     *
     * I cannot use this at the time I have added it due to a dependency cycle
     * this would introduce with DiscardChangesButton.vue
     */
    const getSecondaryComponentValues = () => {
        var _a, _b;
        if (secondaryHeaderComponent) {
            return {
                secondaryComponent: secondaryHeaderComponent.component,
                secondaryComponentProps: secondaryHeaderComponent.props,
            };
        }
        return {
            secondaryComponent: (_a = getSecondaryComponentAndProps(headingParams)) === null || _a === void 0 ? void 0 : _a.component,
            secondaryComponentProps: (_b = getSecondaryComponentAndProps(headingParams)) === null || _b === void 0 ? void 0 : _b.props,
        };
    };
    const secondaryComponentValues = getSecondaryComponentValues();
    const { confirmIcon, confirmText, hideConfirmButton, } = getSchedulingFlowConfirmButtonInfo(headingParams, confirmTo);
    const confirmButtonValues = {
        confirmDisabled: getNextDisabled(headingParams),
        // Takes precedence over confirmTo
        confirmEvent: confirmEventOverride !== null && confirmEventOverride !== void 0 ? confirmEventOverride : getConfirmEvent(headingParams),
        ...(!confirmEventOverride ? { confirmTo } : {}),
        confirmLoading: getConfirmLoading(headingParams),
        confirmText,
        confirmTooltip: getConfirmTooltip(headingParams),
        confirmIcon,
    };
    return {
        backButtonText,
        ...(!hideBackButton ? backValues : {}),
        ...titleValues,
        ...tertiaryComponentValues,
        ...secondaryComponentValues,
        ...(!hideConfirmButton ? confirmButtonValues : {}),
        ...(!withStickyHeader ? { disableStickyHeader: true } : {}),
        ...(withGlobalFooter ? { globalFooter: true } : {}),
        mobileFooter: true,
    };
});

export { schedulingFlowHeadingWithNav };
